import * as React from 'react';

import { DateValue } from '@internationalized/date';
import { today } from '@rematter/common/dateTime';
import { Button, ButtonProps, DatePicker, Popover } from '@rematter/ui-web';
import { useAtom } from 'jotai';

import * as state from '../../_state/loadDetails.state';
import { useTranslations } from '../../i18n/useTranslations';

export type MarkLoadAsReceivedPopoverProps = {
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  loadIds?: string[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

export const MarkLoadAsReceivedPopover = ({
  isOpen,
  loadIds,
  triggerRef,
  onOpenChange,
  onSuccess,
}: MarkLoadAsReceivedPopoverProps) => {
  const t = useTranslations();
  const [date, setDate] = React.useState<DateValue>(today());

  const [{ isSuccess: didMarkLoadAsReceived, isPending }, markLoadAsReceived] =
    useAtom(state.$markLoadAsReceived);

  const handleMarkAsReceived = () => {
    loadIds?.forEach((loadId) => {
      markLoadAsReceived({
        loadId,
        receivedDate: date,
      });
    });
  };

  React.useEffect(() => {
    if (didMarkLoadAsReceived) {
      onOpenChange(false);
      onSuccess();
    }
  }, [didMarkLoadAsReceived, onOpenChange, onSuccess]);

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      triggerRef={triggerRef}
      placement="bottom right"
      className="@MarkLoadAsReceivedPopover"
    >
      <div className="flex flex-col gap-3 p-4">
        <DatePicker
          label={
            loadIds?.length === 1
              ? t.markLoadAsReceived.loadReceivedOn
              : t.markLoadAsReceived.loadsReceivedOn
          }
          isRequired
          value={date}
          onChange={(d) => setDate(d ?? today())}
        />
        <div className="flex justify-between gap-2">
          <Button
            variant={'outline'}
            className={'border-neutral/20'}
            color="neutral"
            onClick={() => onOpenChange(false)}
          >
            {t.markLoadAsReceived.discard}
          </Button>
          <Button onClick={handleMarkAsReceived} isLoading={isPending}>
            {t.markLoadAsReceived.save}
          </Button>
        </div>
      </div>
    </Popover>
  );
};

export type MarkLoadAsReceivedButtonProps = {
  loadIds?: string[];
  variant?: ButtonProps['variant'];
  isDisabled?: boolean;
  hideOnSuccess?: boolean;
};

export const MarkLoadAsReceivedButton = ({
  loadIds,
  variant,
  isDisabled,
  hideOnSuccess = true,
}: MarkLoadAsReceivedButtonProps) => {
  const t = useTranslations();
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [showPopover, setShowPopover] = React.useState(false);
  const [didMarkLoadAsReceived, setDidMarkLoadAsReceived] =
    React.useState(false);

  const shouldHide =
    hideOnSuccess && (didMarkLoadAsReceived || !loadIds?.length);

  React.useEffect(() => {
    setDidMarkLoadAsReceived(false);
  }, [loadIds]);

  if (shouldHide) return null;

  return (
    <div className="@MarkLoadAsReceivedButton contents">
      <Button
        ref={buttonRef}
        onClick={() => setShowPopover((open) => !open)}
        variant={variant}
        color={isDisabled ? 'neutral' : 'primary'}
        isDisabled={isDisabled}
      >
        {t.markLoadAsReceived.markAsReceived}
      </Button>
      <Popover
        isOpen={showPopover}
        onOpenChange={(open) => setShowPopover(open)}
        triggerRef={buttonRef}
        placement="bottom right"
      >
        <MarkLoadAsReceivedPopover
          triggerRef={buttonRef}
          loadIds={loadIds}
          isOpen={showPopover}
          onOpenChange={setShowPopover}
          onSuccess={() => {
            setDidMarkLoadAsReceived(true);
          }}
        />
      </Popover>
    </div>
  );
};
